import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { toLanguage } from '../i18n.js';
import { useSites } from '../pages/Overview.js';
import { fetchExport, save } from './ExportButtons.js';
import { useImprovements } from './Improvements.js';
import { GRADE_STYLE, useLocale, type Grade } from './site.js';
import { Button, Notice, UrgencyBadge } from './ui.js';

/** Lowest score of each grade (monitoring-rules §6). */
const GRADE_FLOOR: Record<Grade, number> = { A: 90, B: 80, C: 65, D: 50, E: 35, F: 0 };

type Tool = 'chat' | 'code' | 'developer';
const TOOLS: readonly Tool[] = ['chat', 'code', 'developer'];

/** The site overview, shared with the dashboard's cache. */
function useOverview(siteId: string) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', { params: { path: { siteId }, query: { locale } } }),
      ),
    refetchInterval: 30_000,
  });
}

/**
 * The side panel of every site page (T-89): progress, the next steps and the fix instructions for
 * a developer or an AI agent. On wide screens it stands right of the page; on narrow ones it is a
 * collapsed panel above it.
 */
export function SiteRail({ siteId, variant }: { siteId: string; variant: 'side' | 'top' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const sections = (
    <>
      <Progress siteId={siteId} />
      <NextSteps siteId={siteId} />
      <AiGuide siteId={siteId} />
    </>
  );
  if (variant === 'side')
    return (
      <aside aria-label={t('rail.label')} className="flex flex-col gap-4 print:hidden">
        {sections}
      </aside>
    );
  return (
    <details
      className="mb-6 rounded-xl border border-slate-200 bg-white print:hidden"
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="flex min-h-12 cursor-pointer items-center gap-2 px-4 font-semibold">
        {t('rail.label')}
      </summary>
      {/* Built only when opened: no hidden copy of the hints next to the page itself. */}
      {open ? (
        <div className="flex flex-col gap-4 border-t border-slate-100 p-3">{sections}</div>
      ) : null}
    </details>
  );
}

function Panel({ title, children, dark }: { title: string; children: ReactNode; dark?: boolean }) {
  return (
    <section
      className={`flex flex-col gap-3 rounded-xl p-4 ${dark ? 'bg-[#0b1b3f] text-white' : 'border border-slate-200 bg-white'}`}
    >
      <h2 className="text-base font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Progress({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const overview = useOverview(siteId);
  const site = useSites().data?.items.find((s) => s.id === siteId);
  const improvements = useImprovements(siteId, 30);
  const health = overview.data?.health;
  if (!health) return null;
  const trend = site?.healthTrend ?? [];
  const delta = trend.length >= 2 ? health.score - (trend[0]?.score ?? health.score) : null;
  const next = health.nextGrade?.grade ?? null;
  const missing = next ? Math.max(0, GRADE_FLOOR[next] - health.score) : null;
  const open = overview.data?.openFindings;
  const urgent = open ? open.critical + open.high : 0;
  const total = open ? open.critical + open.high + open.medium + open.low + open.info : 0;
  const fixed = improvements.data?.items.filter((i) => i.kind === 'fixed').length ?? null;
  const counts: [string, number | null, string, string][] = [
    [t('rail.fixed'), fixed, 'bg-green-50 text-green-900', t('rail.fixedTitle')],
    [t('rail.urgent'), urgent, 'bg-red-50 text-red-900', ''],
    [t('rail.open'), total, 'bg-slate-50 text-slate-900', ''],
  ];
  return (
    <Panel title={t('rail.progress')}>
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className={`grid size-12 shrink-0 place-items-center rounded-xl text-2xl font-extrabold ${GRADE_STYLE[health.grade]}`}
        >
          {health.grade}
        </span>
        <div>
          <p className="text-2xl leading-none font-extrabold tabular-nums">
            {health.score} <span className="text-base font-normal text-slate-700">/ 100</span>
            <span className="sr-only"> ({t('rail.gradeSr', { grade: health.grade })})</span>
          </p>
          <p className="mt-1 text-sm text-slate-700">
            {next && missing !== null
              ? t('rail.toNext', { count: missing, grade: next })
              : t('rail.top')}
          </p>
          {delta !== null && delta !== 0 ? (
            <p className={`text-sm font-semibold ${delta > 0 ? 'text-green-800' : 'text-red-800'}`}>
              {t(delta > 0 ? 'rail.deltaUp' : 'rail.deltaDown', { points: Math.abs(delta) })}
            </p>
          ) : null}
        </div>
      </div>
      {trend.length >= 2 ? (
        <ScoreLine
          points={trend.map((p) => p.score)}
          floor={next ? GRADE_FLOOR[next] : null}
          next={next}
        />
      ) : null}
      <dl className="grid grid-cols-3 gap-2 text-center">
        {counts.map(([label, value, tone, title]) => (
          <div key={label} title={title || undefined} className={`rounded-lg px-2 py-2 ${tone}`}>
            <dd className="text-lg font-extrabold tabular-nums">{value ?? '–'}</dd>
            <dt className="text-xs">
              {label}
              {title ? <span className="sr-only"> ({title})</span> : null}
            </dt>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

/** Health Score of the last 30 days, with the line of the next grade. */
function ScoreLine({
  points,
  floor,
  next,
}: {
  points: readonly number[];
  floor: number | null;
  next: Grade | null;
}) {
  const { t } = useTranslation();
  const w = 300;
  const h = 56;
  const values = floor === null ? points : [...points, floor];
  const lo = Math.max(0, Math.min(...values) - 5);
  const hi = Math.min(100, Math.max(...values) + 5);
  const y = (v: number) => h - 4 - ((v - lo) / Math.max(1, hi - lo)) * (h - 8);
  const x = (i: number) => 4 + (i * (w - 8)) / Math.max(1, points.length - 1);
  const first = points[0] ?? 0;
  const last = points[points.length - 1] ?? 0;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="h-14 w-full"
      role="img"
      aria-label={t('rail.trendSr', { from: first, to: last })}
    >
      {floor !== null && next ? (
        <>
          <line
            x1="4"
            x2={w - 4}
            y1={y(floor)}
            y2={y(floor)}
            stroke="#a16207"
            strokeWidth="1"
            strokeDasharray="4 3"
          />
          <text x={w - 6} y={y(floor) - 4} textAnchor="end" fontSize="10" fill="#854d0e">
            {t('rail.floor', { grade: next, score: floor })}
          </text>
        </>
      ) : null}
      <polyline
        points={points.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
        fill="none"
        stroke="#1d4ed8"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={x(points.length - 1)} cy={y(last)} r="4" fill="#1d4ed8" />
    </svg>
  );
}

function NextSteps({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const overview = useOverview(siteId);
  const top = overview.data?.topFindings ?? [];
  const more = overview.data?.topMore ?? 0;
  if (!overview.data) return null;
  return (
    <Panel title={t('rail.nextSteps')}>
      {top.length === 0 ? (
        <p className="text-sm text-green-900">{t('site.top.empty')}</p>
      ) : (
        <ol className="flex flex-col">
          {top.map((f, i) => (
            <li key={f.id} className="flex gap-3 border-t border-slate-100 py-2.5 first:border-0">
              <span
                aria-hidden="true"
                className="grid size-6 shrink-0 place-items-center rounded-full bg-slate-100 text-sm font-bold"
              >
                {i + 1}
              </span>
              <div className="flex min-w-0 flex-col items-start gap-1">
                <Link
                  to={`/sites/${siteId}/findings?open=${encodeURIComponent(f.ruleCode)}`}
                  className="font-semibold text-slate-900 hover:underline"
                >
                  {f.title}
                </Link>
                <UrgencyBadge urgency={f.urgency} />
              </div>
            </li>
          ))}
        </ol>
      )}
      {more > 0 || top.length > 0 ? (
        <Link
          to={`/sites/${siteId}/findings`}
          className="text-sm font-semibold text-blue-700 hover:underline"
        >
          {t('rail.allProblems')}
        </Link>
      ) : null}
    </Panel>
  );
}

function AiGuide({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const download = useMutation({
    mutationFn: async () => {
      const { blob, name } = await fetchExport(siteId, 'md', toLanguage(i18n.language));
      save(blob, name);
    },
  });
  return (
    <Panel title={t('rail.ai.title')} dark>
      <p className="text-sm text-slate-300">{t('rail.ai.text')}</p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-white px-4 font-semibold text-[#0b1b3f] hover:bg-blue-50 focus-visible:outline-white"
      >
        {t('rail.ai.handOver')}
      </button>
      <button
        type="button"
        onClick={() => download.mutate()}
        disabled={download.isPending}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-500 px-4 font-semibold text-white hover:bg-white/10 focus-visible:outline-white"
      >
        {t('rail.ai.download')}
      </button>
      {download.isError ? (
        <p role="alert" className="text-sm text-red-200">
          {errorMessage(download.error, t)}
        </p>
      ) : null}
      <p className="text-xs text-slate-300">{t('rail.ai.safe')}</p>
      <AgentDialog siteId={siteId} open={open} onClose={() => setOpen(false)} />
    </Panel>
  );
}

/** The task list with an opening that suits the tool, to copy or save (T-89). */
function AgentDialog({
  siteId,
  open,
  onClose,
}: {
  siteId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const textId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const [tool, setTool] = useState<Tool>('chat');
  const [copied, setCopied] = useState(false);
  const [scope, setScope] = useState<'all' | 'urgent'>('all');
  const site = useSites().data?.items.find((s) => s.id === siteId);
  const url = site?.url ?? '';
  const urgent = site ? site.openFindings.critical + site.openFindings.high : null;
  const language = toLanguage(i18n.language);
  const tasks = useQuery({
    queryKey: ['site', siteId, 'findings.md', language, scope],
    queryFn: async () => (await fetchExport(siteId, 'md', language, scope)).blob.text(),
    enabled: open,
  });
  const text = tasks.data ? `${t(`rail.ai.intro.${tool}`, { url })}\n\n${tasks.data}` : '';

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    }
    if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
    if (!open) setCopied(false);
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      document.getElementById(textId)?.focus();
      (document.getElementById(textId) as HTMLTextAreaElement | null)?.select();
    }
  };
  const saveText = () =>
    save(
      new Blob([text], { type: 'text/markdown' }),
      `${t('rail.ai.fileName')}-${tool}${scope === 'urgent' ? '-urgent' : ''}.md`,
    );

  return createPortal(
    <dialog
      ref={dialog}
      aria-labelledby={titleId}
      onClose={onClose}
      className="m-auto w-[calc(100%-2rem)] max-w-2xl rounded-xl p-0 text-slate-900 shadow-xl backdrop:bg-slate-900/55"
    >
      {open ? (
        <div className="flex max-h-[90vh] flex-col gap-4 overflow-y-auto p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <h2 id={titleId} className="text-xl font-bold">
              {t('rail.ai.handOver')}
            </h2>
            <Button variant="secondary" onClick={onClose} aria-label={t('request.close')}>
              ✕
            </Button>
          </div>
          <p className="text-slate-800">{t('rail.ai.dialogText')}</p>
          <div role="group" aria-label={t('rail.ai.toolLabel')} className="flex flex-wrap gap-2">
            {TOOLS.map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={tool === k}
                onClick={() => {
                  setTool(k);
                  setCopied(false);
                }}
                className={`min-h-10 rounded-full px-4 text-sm font-semibold ${tool === k ? 'bg-blue-700 text-white' : 'border border-slate-300 bg-white text-slate-900 hover:bg-blue-50'}`}
              >
                {t(`rail.ai.tools.${k}`)}
              </button>
            ))}
          </div>
          <div role="group" aria-label={t('rail.ai.scopeLabel')} className="flex flex-wrap gap-2">
            {(['all', 'urgent'] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={scope === k}
                onClick={() => {
                  setScope(k);
                  setCopied(false);
                }}
                className={`min-h-10 rounded-lg px-3 text-sm font-semibold ${scope === k ? 'bg-[#0b1b3f] text-white' : 'border border-slate-300 bg-white text-slate-900 hover:bg-blue-50'}`}
              >
                {k === 'urgent' && urgent !== null
                  ? t('rail.ai.scope.urgentCount', { count: urgent })
                  : t(`rail.ai.scope.${k}`)}
              </button>
            ))}
          </div>
          {tasks.isError ? (
            <Notice kind="error">{errorMessage(tasks.error, t)}</Notice>
          ) : (
            <label htmlFor={textId} className="flex flex-col gap-1.5 font-semibold">
              {t('rail.ai.instructions')}
              <textarea
                id={textId}
                readOnly
                rows={12}
                value={tasks.isPending ? t('rail.ai.loading') : text}
                className="resize-y rounded-lg border border-slate-300 bg-slate-50 p-3 font-mono text-sm font-normal"
              />
            </label>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={saveText} disabled={!text}>
              {t('rail.ai.saveFile')}
            </Button>
            <Button onClick={() => void copy()} disabled={!text}>
              {t('rail.ai.copy')}
            </Button>
          </div>
          {copied ? (
            <p role="status" className="text-sm text-green-900">
              {t('rail.ai.copied')}
            </p>
          ) : null}
          <p className="text-sm text-slate-700">{t('rail.ai.safe')}</p>
        </div>
      ) : null}
    </dialog>,
    document.body,
  );
}
